import React from 'react';
import { Card, Row, Col, Statistic, Progress, Table, Tag, Typography, Alert, Divider } from 'antd';
import { DollarOutlined, ThunderboltOutlined, CheckCircleOutlined, CloseCircleOutlined, FieldTimeOutlined, FileExcelOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { API } from '@/services/api.service';
import dayjs from 'dayjs';

const { Text, Paragraph } = Typography;

export default function PerformanceMetricsTab({ config }) {
  const { t } = useTranslation(['tables']);

  const pa = (key) => t(`payrollAutomation.agentConfig.${key}`, { ns: 'tables', defaultValue: key });

  // Fetch detailed metrics for this config
  const { data: metricsData } = useQuery({
    queryKey: ['agent-config-metrics', config?._id],
    queryFn: () => API.agentConfig.getConfigurationMetrics(config._id, {
      startDate: dayjs().subtract(30, 'days').toISOString(),
      endDate: dayjs().toISOString(),
    }),
    enabled: !!config,
  });

  const metrics = metricsData?.data || config?.metrics || {};

  if (!config) {
    return <Alert message={pa('noConfigSelected')} type="info" showIcon />;
  }

  // Calculate derived metrics
  const totalParses = metrics.totalParses || 0;
  const successfulParses = metrics.successfulParses || 0;
  const failedParses = totalParses - successfulParses;
  const successRate = totalParses > 0 ? ((successfulParses / totalParses) * 100).toFixed(1) : 0;
  const avgConfidence = metrics.avgConfidence || 0;
  const avgParsingTime = metrics.avgParsingTime || 0;
  const totalCost = metrics.totalCost || 0;
  const avgCostPerParse = totalParses > 0 ? (totalCost / totalParses).toFixed(3) : 0;

  // File type distribution (mock data - would come from backend)
  const fileTypeData = [
    { fileType: 'MULTI_EMPLOYEE_TIMESHEET', count: Math.floor(totalParses * 0.5), avgConfidence: 87 },
    { fileType: 'NET_SALARY_SUMMARY', count: Math.floor(totalParses * 0.25), avgConfidence: 92 },
    { fileType: 'WAGE_NOTIFICATION', count: Math.floor(totalParses * 0.15), avgConfidence: 85 },
    { fileType: 'ATTENDANCE_SHEET', count: Math.floor(totalParses * 0.1), avgConfidence: 78 },
  ].filter((item) => item.count > 0);

  const fileTypeColumns = [
    {
      title: pa('fileType'),
      dataIndex: 'fileType',
      key: 'fileType',
      render: (type) => <Tag color="blue">{type.replace(/_/g, ' ')}</Tag>,
    },
    {
      title: pa('count'),
      dataIndex: 'count',
      key: 'count',
    },
    {
      title: pa('avgConfidence'),
      dataIndex: 'avgConfidence',
      key: 'avgConfidence',
      render: (confidence) => {
        const color = confidence >= 80 ? 'green' : confidence >= 60 ? 'orange' : 'red';
        return <Tag color={color}>{confidence}%</Tag>;
      },
    },
    {
      title: pa('percentage'),
      key: 'percentage',
      render: (_, record) => {
        const percentage = totalParses > 0 ? ((record.count / totalParses) * 100).toFixed(1) : 0;
        return <Progress percent={parseFloat(percentage)} size="small" />;
      },
    },
  ];

  // Common errors/warnings (mock data)
  const commonIssues = metrics.commonErrors || [
    { issue: 'Employee name split across cells', count: 12, severity: 'warning' },
    { issue: 'Missing hours for employee', count: 8, severity: 'error' },
    { issue: 'Ambiguous date format', count: 5, severity: 'warning' },
    { issue: 'Merged cells detected', count: 15, severity: 'info' },
  ];

  const issuesColumns = [
    {
      title: pa('issue'),
      dataIndex: 'issue',
      key: 'issue',
    },
    {
      title: pa('occurrences'),
      dataIndex: 'count',
      key: 'count',
      width: 120,
    },
    {
      title: pa('severity'),
      dataIndex: 'severity',
      key: 'severity',
      width: 100,
      render: (severity) => {
        const colors = { error: 'red', warning: 'orange', info: 'blue' };
        return <Tag color={colors[severity]}>{severity}</Tag>;
      },
    },
  ];

  const confidenceColor = avgConfidence >= 80 ? 'green' : avgConfidence >= 60 ? 'orange' : 'red';
  const successRateColor = successRate >= 80 ? 'green' : successRate >= 60 ? 'orange' : 'red';

  return (
    <div>
      {/* Overall Performance Summary */}
      <Card title={<><ThunderboltOutlined /> {pa('performanceOverview')}</>} style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Statistic
              title={pa('totalParses')}
              value={totalParses}
              prefix={<FileExcelOutlined />}
              valueStyle={{ fontSize: 24 }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title={pa('successRate')}
              value={successRate}
              suffix="%"
              valueStyle={{ color: successRateColor, fontSize: 24 }}
              prefix={<CheckCircleOutlined />}
            />
            <div style={{ marginTop: 8 }}>
              <Progress
                percent={parseFloat(successRate)}
                strokeColor={successRateColor}
                showInfo={false}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <CheckCircleOutlined style={{ color: 'green' }} /> {successfulParses} {pa('successful')}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <CloseCircleOutlined style={{ color: 'red' }} /> {failedParses} {pa('failed')}
                </Text>
              </div>
            </div>
          </Col>
          <Col span={6}>
            <Statistic
              title={pa('avgConfidence')}
              value={avgConfidence.toFixed(1)}
              suffix="%"
              valueStyle={{ color: confidenceColor, fontSize: 24 }}
            />
            <div style={{ marginTop: 8 }}>
              <Progress
                percent={avgConfidence}
                strokeColor={confidenceColor}
                showInfo={false}
              />
            </div>
          </Col>
          <Col span={6}>
            <Statistic
              title={pa('avgParsingTime')}
              value={(avgParsingTime / 1000).toFixed(1)}
              suffix="s"
              prefix={<FieldTimeOutlined />}
              valueStyle={{ fontSize: 24 }}
            />
          </Col>
        </Row>
      </Card>

      {/* Cost Analysis */}
      <Card title={<><DollarOutlined /> {pa('costAnalysis')}</>} style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <Statistic
              title={pa('totalCost')}
              value={totalCost}
              prefix="$"
              precision={2}
              valueStyle={{ fontSize: 24 }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title={pa('avgCostPerParse')}
              value={avgCostPerParse}
              prefix="$"
              precision={3}
              valueStyle={{ fontSize: 24 }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title={pa('estimatedMonthlyCost')}
              value={(avgCostPerParse * totalParses).toFixed(2)}
              prefix="$"
              precision={2}
              valueStyle={{ fontSize: 24 }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {pa('basedOn')} {totalParses} {pa('parses')}
            </Text>
          </Col>
        </Row>

        <Divider />

        <div>
          <Text strong>{pa('budgetUsage')}</Text>
          <div style={{ marginTop: 8 }}>
            <Progress
              percent={config.budgetUsagePercent || 0}
              status={config.budgetUsagePercent >= 80 ? 'exception' : 'normal'}
              strokeColor={config.budgetUsagePercent >= 80 ? 'red' : config.budgetUsagePercent >= 60 ? 'orange' : 'green'}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                ${config.budget?.currentMonthSpend || 0} {pa('spent')}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                ${config.budget?.monthlyCap || 500} {pa('cap')}
              </Text>
            </div>
          </div>
        </div>
      </Card>

      {/* File Type Distribution */}
      {fileTypeData.length > 0 && (
        <Card title={pa('fileTypeDistribution')} style={{ marginBottom: 16 }}>
          <Table
            columns={fileTypeColumns}
            dataSource={fileTypeData}
            rowKey="fileType"
            pagination={false}
            size="small"
          />
        </Card>
      )}

      {/* Common Issues */}
      <Card title={pa('commonIssues')}>
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          {pa('commonIssuesDesc')}
        </Paragraph>
        <Table
          columns={issuesColumns}
          dataSource={commonIssues}
          rowKey="issue"
          pagination={false}
          size="small"
        />

        {commonIssues.length === 0 && (
          <Alert
            message={pa('noIssuesFound')}
            description={pa('noIssuesFoundDesc')}
            type="success"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </Card>

      {/* Last Used Info */}
      {metrics.lastUsed && (
        <Card style={{ marginTop: 16 }}>
          <Text type="secondary">
            {pa('lastUsed')}: {dayjs(metrics.lastUsed).format('DD.MM.YYYY HH:mm')} (
            {dayjs(metrics.lastUsed).fromNow()})
          </Text>
        </Card>
      )}
    </div>
  );
}
